import { CustomerOrderDataGridApi_CustomerOrderDto } from "src/api/generated/erp/sales/customerOrder/api/customerOrderDataGridApi.ts";
import { CustomerOrderActionApi } from "src/api/generated/erp/sales/customerOrder/api/customerOrderActionApi.ts";
import { dayJsToDateIsoString } from "src/utils/dayjsUtils.ts";
import i18n from "i18next";
import { ProjectQueryApi } from "src/api/generated/erp/project/project/api/projectQueryApi.ts";
import { CustomerOrderQueryApi } from "src/api/generated/erp/sales/customerOrder/api/customerOrderQueryApi.ts";
import { OpenInputDialogFunc } from "../../../../common/dialogs/input/useInputDialog.tsx";
import { ShowConfirmDialogFunc } from "src/components/common/dialogs/confirmDialog/ConfirmDialogContext.ts";

export async function createProjectForCustomerOrder(
	row: CustomerOrderDataGridApi_CustomerOrderDto,
	showInputDialog: OpenInputDialogFunc,
	showConfirmDialog: ShowConfirmDialogFunc,
) {
	const shouldContinue = await confirmIfConnectedToOtherProjects();
	if (!shouldContinue) return;

	const templateProjectId = await askModelProject();
	const startDate = await askStartDate();
	if (templateProjectId == null || startDate == null) return;
	await CustomerOrderActionApi.createProjectFromCustomerOrder({
		customerOrderId: row.customerOrderId,
		templateProjectId: templateProjectId,
		plannedStartDate: dayJsToDateIsoString(startDate),
	});

	function askModelProject() {
		return showInputDialog({
			defaultValue: null,
			type: "singleNumberSelect",
			title: i18n.t("select_model_project"),
			fieldLabel: i18n.t("model_project"),
			required: true,
			fieldProps: {
				selection: {
					options: async ({ currentSelection, searchQuery }) => {
						const modelProjects = await ProjectQueryApi.getProjectSelectionOptions({
							siteId: row.siteId,
							currentSelection,
							searchQuery,
						});
						return modelProjects.map((x) => ({
							value: x.projectId,
							label: x.projectDescription,
						}));
					},
				},
			},
		});
	}

	async function confirmIfConnectedToOtherProjects() {
		const linkedProjects = await ProjectQueryApi.getProjectsConnectedToCustomerOrder({
			customerOrderId: row.customerOrderId,
		});
		if (linkedProjects.length === 0) return true;

		return await showConfirmDialog({
			title: i18n.t("warning"),
			message: i18n.t("customer_order_already_connected_to_projects_warning", {
				projects: linkedProjects.map((project) => `${project.projectId} | ${project.projectDescription}`).join(", "),
			}),
			confirmButtonText: i18n.t("continue"),
		});
	}

	function askStartDate() {
		return showInputDialog({
			defaultValue: null,
			type: "date",
			title: i18n.t("select_start_date"),
			fieldLabel: i18n.t("start_date"),
			required: true,
		});
	}
}

export async function connectCustomerOrderToProject(
	row: CustomerOrderDataGridApi_CustomerOrderDto,
	showInputDialog: OpenInputDialogFunc,
	showConfirmDialog: ShowConfirmDialogFunc,
) {
	const projectId = await askProject();
	if (projectId == null) return;

	const shouldContinue = await confirmIfConnectedToOtherProjects(projectId);
	if (!shouldContinue) return;
	await CustomerOrderActionApi.connectCustomerOrderToProject({
		customerOrderId: row.customerOrderId,
		projectId: projectId,
	});

	async function confirmIfConnectedToOtherProjects(selectedProjectId: number) {
		const linkedProjects = await ProjectQueryApi.getProjectsConnectedToCustomerOrder({
			customerOrderId: row.customerOrderId,
		});
		const otherProjects = linkedProjects.filter((project) => project.projectId !== selectedProjectId);
		if (otherProjects.length === 0) return true;

		return await showConfirmDialog({
			title: i18n.t("warning"),
			message: i18n.t("customer_order_already_connected_to_projects_warning", {
				projects: otherProjects.map((project) => `${project.projectId} | ${project.projectDescription}`).join(", "),
			}),
			confirmButtonText: i18n.t("continue"),
		});
	}

	function askProject() {
		return showInputDialog({
			defaultValue: null,
			type: "singleNumberSelect",
			title: i18n.t("connect_to_project"),
			fieldLabel: i18n.t("project"),
			required: true,
			fieldProps: {
				selection: {
					options: async ({ searchQuery, currentSelection }) => {
						const projects = await ProjectQueryApi.getProjectSelectionOptions({
							searchQuery: searchQuery,
							currentSelection: currentSelection,
							siteId: row.siteId,
							includeTemplates: false,
						});
						return projects.map((x) => ({
							value: x.projectId,
							label: x.projectDescription,
						}));
					},
				},
			},
		});
	}
}

export async function copyCustomerOrderTasks(
	row: CustomerOrderDataGridApi_CustomerOrderDto,
	showInputDialog: OpenInputDialogFunc,
) {
	const sourceCustomerOrderId = await askSourceCustomerOrder();
	if (sourceCustomerOrderId == undefined) return;
	await CustomerOrderActionApi.copyCustomerOrderTasks({
		sourceCustomerOrderId: sourceCustomerOrderId,
		targetCustomerOrderId: row.customerOrderId,
	});

	function askSourceCustomerOrder() {
		return showInputDialog({
			defaultValue: null,
			type: "singleNumberSelect",
			title: i18n.t("copy_tasks_from_customer_order"),
			fieldLabel: i18n.t("customer_order"),
			required: true,
			fieldProps: {
				selection: {
					options: async ({ searchQuery, currentSelection }) => {
						const customerOrders = await CustomerOrderQueryApi.getCustomerOrderOptions({
							searchQuery: searchQuery,
							siteId: row.siteId,
							currentSelection: currentSelection,
						});
						return customerOrders.map((order) => ({
							value: order.customerOrderId,
							label: `${order.customerOrderId} | ${order.orderReference}`,
						}));
					},
				},
			},
		});
	}
}
